import React, { useState } from 'react';
import { Phone, Send, CheckCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MATERIAL_OPTIONS = [
  { value: '', label: 'Type de matériel' },
  { value: 'Chapiteau / Barnum', label: 'Chapiteau / Barnum' },
  { value: 'Mobilier événementiel', label: 'Mobilier (tables, chaises, vaisselle)' },
  { value: 'Sono / Éclairage', label: 'Sono / Éclairage' },
  { value: 'Structure gonflable', label: 'Structure gonflable / Animations' },
  { value: 'Bricolage / Travaux', label: 'Bricolage / Travaux' },
  { value: 'Jardinage / Motoculture', label: 'Jardinage / Motoculture' },
  { value: 'Nettoyage', label: 'Nettoyage haute pression' },
  { value: 'Autre', label: 'Autre / Je ne sais pas encore' }
];

const QuickQuoteWidget = ({ defaultCity = '', title, subtitle, className = '' }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    city: defaultCity,
    material: ''
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const validatePhone = (phone) => /^(?:\+33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.name.trim() || !form.phone.trim() || !form.city.trim()) {
      setErrorMsg('Merci de remplir nom, téléphone et ville.');
      return;
    }
    if (!validatePhone(form.phone)) {
      setErrorMsg('Numéro de téléphone français invalide (ex: 06 12 34 56 78).');
      return;
    }

    setStatus('loading');
    try {
      const response = await fetch(`${API_URL}/api/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: '',
          phone: form.phone,
          service: form.material || 'Non précisé',
          location: form.city,
          message: `[Devis express] ${form.material || 'Matériel non précisé'} à ${form.city}.`,
          source: 'widget-devis-30s'
        })
      });
      if (!response.ok) throw new Error('Erreur serveur');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Impossible d\'envoyer votre demande. Réessayez ou appelez-nous.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`bg-green-50 border-2 border-green-400 rounded-2xl p-8 text-center ${className}`} data-testid="quick-quote-success">
        <CheckCircle className="text-green-500 mx-auto mb-4" size={56} />
        <h3 className="text-2xl font-bold text-slate-800 mb-2">Demande reçue !</h3>
        <p className="text-slate-600 mb-4">
          Nous vous rappelons sous 24h au <strong>{form.phone}</strong>.<br />
          Pour une demande urgente, appelez-nous directement.
        </p>
        <a
          href="tel:+33623893298"
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors"
          data-testid="quick-quote-success-call"
        >
          <Phone size={18} /> 06 23 89 32 98
        </a>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8 ${className}`}
      data-testid="quick-quote-widget"
    >
      <div className="flex items-center gap-2 text-green-600 mb-2">
        <Clock size={18} />
        <span className="text-sm font-semibold uppercase tracking-wide">Devis express en 30 secondes</span>
      </div>
      <h3 className="text-2xl sm:text-3xl font-black text-slate-800 mb-1">
        {title || 'Obtenez votre devis gratuit'}
      </h3>
      <p className="text-slate-600 mb-6">
        {subtitle || 'On vous rappelle sous 24h. Pas d\'email requis.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-3" data-testid="quick-quote-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Votre nom *"
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            data-testid="quick-quote-name"
            required
          />
          <input
            type="tel"
            value={form.phone}
            onChange={handleChange('phone')}
            placeholder="Téléphone * (06...)"
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            data-testid="quick-quote-phone"
            required
          />
        </div>
        <input
          type="text"
          value={form.city}
          onChange={handleChange('city')}
          placeholder="Ville de livraison *"
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          data-testid="quick-quote-city"
          required
        />
        <select
          value={form.material}
          onChange={handleChange('material')}
          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          data-testid="quick-quote-material"
        >
          {MATERIAL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {errorMsg && (
          <p className="text-red-600 text-sm" data-testid="quick-quote-error">{errorMsg}</p>
        )}

        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white font-bold rounded-xl transition-colors text-lg"
          data-testid="quick-quote-submit"
        >
          {status === 'loading' ? (
            <>Envoi en cours...</>
          ) : (
            <>
              <Send size={20} /> Recevoir mon devis
            </>
          )}
        </button>

        <p className="text-xs text-slate-500 text-center">
          🔒 Vos données restent confidentielles. Aucun spam, juste votre devis.
        </p>
      </form>
    </div>
  );
};

export default QuickQuoteWidget;
